import React from 'react'
import { withAllContexts } from '../../../AllContextsWrapper';
import { Euro, TrendingUp, CreditCard, AlertCircle } from 'lucide-react';
import moment from 'moment';
import PluginRegistry from '../../../plugins';

class Overview extends React.Component {
  render() {
    let reservation = this.props.reservation;
    let feesPrice = 0;
    let paymentsAmount = 0;

    const fees = reservation.Fees || [];
    const payments = reservation.Payments || [];

    fees.forEach((fee) => {
      feesPrice = feesPrice + (fee.amount * fee.price);
    });

    payments.forEach((payment) => {
      paymentsAmount = paymentsAmount + payment.amount;
    });

    const plugin = PluginRegistry.get(this.props.app.state.activeCalendar?.type || 'rafting');
    const PluginOverviewSection = plugin.OverviewSection;
    const additionalPrice = plugin.calculatePrice ? plugin.calculatePrice(reservation, this.props.reservationLength, this.props.app) : 0;

    const totalPrice = feesPrice + additionalPrice;
    const remaining = totalPrice - paymentsAmount;
    const isPaid = remaining <= 0;

    const isCamping = this.props.app.state.activeCalendar?.type === 'accommodation';
    const bgClass = isCamping ? 'bg-taborisko-pine/5 border-taborisko-pine/10' : 'bg-splavma-teal/5 border-splavma-teal/10';
    const textPrimaryClass = isCamping ? 'text-taborisko-pine' : 'text-splavma-teal';
    const iconClass = isCamping ? 'text-taborisko-orange' : 'text-splavma-orange';
    const borderClass = isCamping ? 'border-taborisko-pine/10' : 'border-splavma-teal/10';

    return (
      <div className="space-y-6">
        {/* Price Overview Card */}
        <div className={`p-5 md:p-6 border shadow-sm ${bgClass}`}>
          <h3 className={`text-sm font-bold uppercase tracking-wide mb-5 flex items-center gap-3 ${textPrimaryClass}`}>
            <Euro className="w-5 h-5" />
            Cenový prehľad
          </h3>

          <div className="space-y-1">
            {/* Fees */}
            <div className={`flex items-center justify-between py-2.5 border-b ${borderClass}`}>
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${isCamping ? 'bg-taborisko-orange' : 'bg-splavma-orange'}`}></div>
                <span className="text-sm font-medium text-gray-700">Poplatky za služby</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{feesPrice.toFixed(2)} €</span>
            </div>

            {/* Plugin-specific pricing */}
            {PluginOverviewSection && (
              <div className={`border-b ${borderClass}`}>
                <PluginOverviewSection
                  reservation={reservation}
                  reservationLength={this.props.reservationLength}
                  app={this.props.app}
                />
              </div>
            )}

            {/* Payments */}
            <div className={`flex items-center justify-between py-2.5 border-b ${borderClass}`}>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-success-500"></div>
                <span className="text-sm font-medium text-gray-700">Uhradené platby</span>
              </div>
              <span className="text-sm font-bold text-success-600">{paymentsAmount.toFixed(2)} €</span>
            </div>

            {/* Remaining */}
            <div className={`flex items-center justify-between py-4 px-4 mt-4 rounded-xl border shadow-sm ${isPaid ? 'bg-success-50 border-success-100' : 'bg-orange-50 border-orange-100'}`}>
              <div className="flex items-center gap-3">
                {isPaid ? (
                  <TrendingUp className="w-5 h-5 text-success-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                )}
                <span className={`text-sm font-bold uppercase tracking-wider ${isPaid ? 'text-success-700' : 'text-orange-700'}`}>
                  {isPaid ? 'Kompletne uhradené' : 'Zostáva doplatiť'}
                </span>
              </div>
              <span className={`text-xl font-black ${isPaid ? 'text-success-700' : 'text-orange-700'}`}>
                {remaining.toFixed(2)} €
              </span>
            </div>
          </div>
        </div>

        {/* Related Reservations */}
        {this.props.relatedReservations && this.props.relatedReservations.length > 1 && (
          <div className="bg-white shadow-sm border border-gray-100 p-5 md:p-6">
            <h3 className={`text-sm font-bold uppercase tracking-wide mb-4 flex items-center gap-2 ${textPrimaryClass}`}>
              Súvisiace rezervácie
            </h3>
            <div className="space-y-3">
              {this.props.relatedReservations.map(res => {
                if (res.id === reservation.id) return null;

                const statusColors = {
                  ordered: 'bg-blue-50 text-blue-700 border-blue-100',
                  onwater: 'bg-green-50 text-green-700 border-green-100',
                  finished: 'bg-gray-50 text-gray-700 border-gray-200',
                  canceled: 'bg-red-50 text-red-700 border-red-100',
                };

                return (
                  <button
                    key={res.id}
                    onClick={() => this.props.app.functions.updateCurrReservation(res.id)}
                    className="w-full flex items-center justify-between p-4 border border-gray-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all group shadow-sm hover:shadow"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded flex items-center justify-center transition-colors font-black ${isCamping ? 'bg-taborisko-pine/10 text-taborisko-pine group-hover:bg-taborisko-pine/20' : 'bg-splavma-teal/10 text-splavma-teal group-hover:bg-splavma-teal/20'}`}>
                        #{res.id}
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-900 mb-1">Rezervácia #{res.id}</p>
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${statusColors[res.status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                          {res.status}
                        </span>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-500">
                      {moment.utc(res.start_date).local().format('D.M.YYYY')}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withAllContexts(Overview);
